import { AtlasMenuContentItem, Icon, Menu } from "@resource/atlas";
import { atlasChevronDown } from "@resource/atlas/icons";
import { useMemo } from "react";

import { StageStepStage } from "./index";
import StepIndicator from "./StepIndicator";

type MobileStageStepperProps = {
  stages: StageStepStage[];
  allowNavigationToLockedStages: boolean;
  selectedStageId: string;
  currentStageId?: string;
  onSelectStageId(stageId: string): void;
  interactive: boolean;
};

function MobileStageStepper({
  stages,
  allowNavigationToLockedStages,
  selectedStageId,
  currentStageId,
  onSelectStageId,
  interactive,
}: MobileStageStepperProps) {
  const currentIndex = stages.findIndex((stage) => stage.id === currentStageId);
  const activeIndex = stages.findIndex((stage) => stage.id === selectedStageId);

  const items = useMemo(
    () =>
      stages.map((stage, index): AtlasMenuContentItem => {
        const isLocked = index > currentIndex;
        const isActive = index === activeIndex;
        const isCurrent = index === currentIndex;
        const subtitle = stage.needsFeedback
          ? "Please rate your interview."
          : stage.description ?? undefined;

        return {
          _type: "item",
          key: stage.id,
          children: stage.title,
          onSelect: () => onSelectStageId(stage.id),
          isSelectable: true,
          isSelected: isActive,
          disabled:
            !interactive || (isLocked && !allowNavigationToLockedStages),
          renderContent: ({ children }) => (
            <div className="flex gap-2">
              <StepIndicator isCurrent={isCurrent} isLocked={isLocked} />
              <div>
                <p>
                  {children}
                  {stage.needsFeedback && (
                    <b className="text-warning-700 whitespace-nowrap"> *</b>
                  )}
                </p>
                {subtitle && (
                  <p className="text-body-sm text-subtle mt-[0.125rem]">
                    {subtitle}
                  </p>
                )}
              </div>
            </div>
          ),
        };
      }),
    [
      activeIndex,
      currentIndex,
      onSelectStageId,
      stages,
      allowNavigationToLockedStages,
      interactive,
    ]
  );

  const activeStage = stages[activeIndex];

  return (
    <div className="flex flex-col gap-2 my-8">
      <p className="text-body-lg-heavy">Stage</p>
      <Menu.Root>
        <Menu.Trigger>
          {/* eslint-disable-next-line react/button-has-type */}
          <button className="flex bg-light-gray-500 hover:bg-light-gray-600 active:bg-light-gray-700 rounded-md w-full text-left text-body-md text-dark p-[.625rem] focus:outline-none focus-visible:focus-ring focus-visible:bg-light-gray-600">
            <span className="flex-grow">
              {activeStage.title}
              {activeStage.needsFeedback && (
                <b className="text-warning-700 whitespace-nowrap"> *</b>
              )}
            </span>
            <Icon content={atlasChevronDown} />
          </button>
        </Menu.Trigger>
        <Menu.Content
          className="w-[var(--popover-anchor-width)]"
          defaultSize="compact"
          items={items}
        />
      </Menu.Root>
    </div>
  );
}

export default MobileStageStepper;
